import { EnhancedProvider } from 'containers/Scheduling/types'
import { Logger } from 'utils/logger.utils'
import { SLOT_CONSTANTS, PHASES } from './constants'
import { SlotPhaseData } from './types'
import {
  analyzeProviderSlots,
  getSlotsForPhase,
  sortSlotsByTime,
  hasMoreSlotsAvailable,
  getButtonText,
} from './utils'

/**
 * Calculates all slot phase data for a provider
 */
export const calculateSlotPhaseData = (
  provider: EnhancedProvider,
  currentPhase: number,
  providersForSelectedDate?: EnhancedProvider[],
  logger?: Logger,
  timeZone?: string
): SlotPhaseData => {
  // Analyze provider slots and determine starting penalty level
  const {
    refSlots,
    penaltyLevel,
    penaltyCounts,
    timeBasedSlots,
  } = analyzeProviderSlots(provider, timeZone)

  // Get slots for current phase
  const { slots: currentSlots, usedPenaltyLevels } = getSlotsForPhase(
    provider,
    currentPhase,
    penaltyCounts,
    timeBasedSlots
  )

  // Sort slots by time
  const sortedSlots = sortSlotsByTime(currentSlots)

  // Calculate total available slots across all penalty levels
  const totalAvailableSlots =
    provider.noPenaltySlots.length +
    provider.mediumPenaltySlots.length +
    provider.highPenaltySlots.length

  // Calculate layout data
  const slotsByRow = SLOT_CONSTANTS.SLOTS_BY_ROW
  const totalRows = Math.ceil(sortedSlots.length / slotsByRow)
  const visibleRows =
    currentPhase === PHASES.LIMITED_SLOTS
      ? sortedSlots.length > 0
        ? 2
        : 0 // Show 2 rows if slots exist, 0 if no slots
      : totalRows // Show all rows for other phases

  // Check if more slots are available
  const hasMoreSlots = hasMoreSlotsAvailable(
    currentPhase,
    penaltyLevel,
    penaltyCounts
  )

  // Check if we can go back
  const canGoBack = currentPhase > PHASES.LIMITED_SLOTS

  // Log slot information for monitoring
  if (logger) {
    logger.info(
      {
        noPenalty: provider.noPenaltySlots.length,
        mediumPenalty: provider.mediumPenaltySlots.length,
        highPenalty: provider.highPenaltySlots.length,
        currentSlots: currentSlots.length,
        sortedSlots: sortedSlots.length,
        visibleRows,
        totalRows,
        phase: currentPhase,
        providerId: provider.id,
        providerEmail: provider.email,
        totalAvailableSlots,
        hasMoreSlots,
        usedPenaltyLevels,
        canGoBack,
      },
      'calculateSlotPhaseData'
    )
  }

  return {
    sortedSlots,
    visibleRows,
    slotsByRow,
    hasMoreSlots,
    canGoBack,
    currentPhase,
    getButtonText,
    usedPenaltyLevels,
    totalAvailableSlots,
  }
}
